import { Box, Typography, Grid, Button } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import ButtonComp from "../../../../../components/Button";
import { string } from "../../../../../constants/string";
import cloudUpload from "../../../../../assets/images/cloudUpload.svg";
import { useTenPercentClaimFormStore } from "../../../../../store/useTenPercentClaimFormStore";

export default function DocumentVerification({
  handleTenPercentDocumentVerificationNext,
  handleBack,
}: any) {
  const [status, setStatus] = useState("Pending");
  const [identityFileName, setIdentityFileName] = useState("");
  const [selfieFileName, setSelfieFileName] = useState("");
  const [livenessFileName, setLivenessFileName] = useState("");
  const [fileErrors, setFileErrors] = useState("");

  const identityInputRef = useRef<HTMLInputElement>(null);
  const selfieInputRef = useRef<HTMLInputElement>(null);
  const livenessInputRef = useRef<HTMLInputElement>(null);
  const affairInputRef = useRef<HTMLInputElement>(null);

  const addTenPercentDocument = useTenPercentClaimFormStore(
    (state) => state.addTenPercentDocument
  );
  const tenPercentdocuments = useTenPercentClaimFormStore(
    (state) => state.tenPercentdocuments
  );

  const handleFileUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    key:
      | "identity_document"
      | "selfie_with_identity"
      | "liveness_check"
      | "home_affairs_verification"
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      addTenPercentDocument(key, file);
      if (key === "identity_document") {
        setIdentityFileName(file.name);
        setFileErrors("");
      }
      if (key === "selfie_with_identity") setSelfieFileName(file.name);
      if (key === "liveness_check") setLivenessFileName(file.name);
    }
  };

  const handleStatusRefresh = () => {
    setStatus("Verified"); // mock refresh logic
  };

  useEffect(() => {
    if (tenPercentdocuments.identity_document) {
      setIdentityFileName(tenPercentdocuments.identity_document.name);
    }
    if (tenPercentdocuments.selfie_with_identity) {
      setSelfieFileName(tenPercentdocuments.selfie_with_identity.name);
    }
    if (tenPercentdocuments.liveness_check) {
      setLivenessFileName(tenPercentdocuments.liveness_check.name);
    }
  }, []);

  return (
    <Box sx={{ py: 4 }} width="100%">
      <Grid container spacing={3}>
        {/* Identity Document Upload */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              border: "2px dashed #ccc",
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              height: "100%",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.10)",
            }}
          >
            <img src={cloudUpload} alt="cloudUpload" />
            <Typography fontWeight={400} fontSize={16} lineHeight={2}>
              Upload identity document
            </Typography>
            <Typography fontSize={12} color="#6D6D6D" mb={2}>
              Accepted: .jpeg, .png, .pdf
            </Typography>

            <input
              ref={identityInputRef}
              type="file"
              accept="image/*,.pdf"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  if (file.size <= 2 * 1024 * 1024) {
                    handleFileUpload(e, "identity_document");
                    setFileErrors("");
                  } else {
                    setFileErrors("File must be less than 2MB.");
                  }
                }
              }}
            />
            <Button
              variant="contained"
              onClick={() => identityInputRef.current?.click()}
            >
              Choose File
            </Button>
            <Typography mt={1} variant="body2" color="#929191">
              {identityFileName}
            </Typography>
            {fileErrors && (
              <Typography mt={2} color="error" fontSize={14}>
                {fileErrors}
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Selfie Upload */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              border: "2px dashed #ccc",
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              height: "100%",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.10)",
            }}
          >
            <img src={cloudUpload} alt="cloudUpload" />
            <Typography fontWeight={400} fontSize={16} lineHeight={2}>
              Upload selfie with identity document
            </Typography>
            <Typography fontSize={12} color="#6D6D6D" mb={2}>
              Accepted: .jpeg, .png, .pdf
            </Typography>

            <input
              ref={selfieInputRef}
              type="file"
              accept="image/*,.pdf"
              hidden
              onChange={(e) => handleFileUpload(e, "selfie_with_identity")}
            />
            <Button
              variant="contained"
              onClick={() => selfieInputRef.current?.click()}
            >
              Choose File
            </Button>
            <Typography mt={1} variant="body2" color="#929191">
              {selfieFileName}
            </Typography>
          </Box>
        </Grid>

        {/* Liveness Check Upload */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              border: "2px dashed #ccc",
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              height: "100%",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.10)",
            }}
          >
            <img src={cloudUpload} alt="cloudUpload" />
            <Typography fontWeight={400} fontSize={16} lineHeight={2}>
              Upload liveness check
            </Typography>
            <Typography fontSize={12} color="#6D6D6D" mb={2}>
              Accepted: .jpeg, .mp4
            </Typography>

            <input
              ref={livenessInputRef}
              type="file"
              accept="image/*,.mp4"
              hidden
              onChange={(e) => handleFileUpload(e, "liveness_check")}
            />
            <Button
              variant="contained"
              onClick={() => livenessInputRef.current?.click()}
            >
              Choose File
            </Button>
            <Typography mt={1} variant="body2" color="#929191">
              {livenessFileName}
            </Typography>
          </Box>
        </Grid>

        {/* Verification Status */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              border: "2px dashed #ccc",
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              height: "100%",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.10)",
            }}
          >
            <Typography fontWeight={400} mb={2} fontSize={16} px={6}>
              Verifying uploaded documents & selfie via Home Affairs
            </Typography>
            <Typography fontSize="14px" mb={1} fontWeight={600} color="#6D6D6D">
              Status: {status}
            </Typography>
            <ButtonComp
              variant="contained"
              onClick={handleStatusRefresh}
              height={30}
              fontSize="16px"
              width={170}
              textTransform="none"
            >
              Refresh Status
            </ButtonComp>
            <input
              ref={affairInputRef}
              type="file"
              accept="image/*,.mp4"
              hidden
              onChange={(e) => handleFileUpload(e, "home_affairs_verification")}
            />
          </Box>
        </Grid>
      </Grid>

      {/* Navigation Buttons */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          py: 6,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2 },
        }}
      >
        <ButtonComp
          variant="outlined"
          height={40}
          fontSize="16px"
          width={200}
          onClick={handleBack}
        >
          {string.claims.back}
        </ButtonComp>
        <ButtonComp
          variant="contained"
          height={40}
          fontSize="16px"
          width={200}
          onClick={() => {
            if (!tenPercentdocuments.identity_document) {
              setFileErrors("Please upload identity document");
              return;
            }
            setFileErrors("");
            handleTenPercentDocumentVerificationNext(tenPercentdocuments);
          }}
        >
          {string.claims.next}
        </ButtonComp>
      </Box>
    </Box>
  );
}
